import { useEffect, useMemo, useRef, useState } from 'react';
import {
  estimateDownload,
  formatNumber,
  type Bbox,
  type DownloadEstimate,
} from '@boatnav/core';
import { MapShell, useMapController } from '../map/MapShell.js';
import { useBaseLayerStore, baseTilePath } from '../map/baseLayerStore.js';
import {
  REGION_ZOOM_MIN,
  REGION_ZOOM_MAX,
  REGION_MAX_TILES,
} from './constants.js';
import { startRegionDownload, type DownloadProgress } from './downloadEngine.js';
import { DEPTH_Z } from '../route/depthProvider.js';
import { loadDepthEdition } from '../route/depthMetaStore.js';
import { uuid } from '../../lib/uuid.js';
import { formatBytes } from '../../lib/platform.js';
import { type DownloadRegionResponse } from '@boatnav/schemas';
import { msg } from '../../lib/messages.js';
import './region-picker.css';

const BOX_W = 0.8;
const BOX_H = 0.5;

type Phase = 'pick' | 'downloading' | 'done';

async function requestToken(bbox: Bbox): Promise<string | null> {
  try {
    const res = await fetch('/api/downloads/region', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ bbox, zMin: REGION_ZOOM_MIN, zMax: REGION_ZOOM_MAX }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as DownloadRegionResponse;
    return data.token;
  } catch {
    return null;
  }
}

/** Chart estimate + the z13-only depth pack that rides along with it (spec 10). */
function combinedEstimate(bbox: Bbox): DownloadEstimate {
  const chart = estimateDownload(bbox, REGION_ZOOM_MIN, REGION_ZOOM_MAX);
  const depth = estimateDownload(bbox, DEPTH_Z, DEPTH_Z);
  return { tileCount: chart.tileCount + depth.tileCount, bytes: chart.bytes + depth.bytes };
}

function Estimator({ onEstimate }: { onEstimate: (e: DownloadEstimate, b: Bbox) => void }): null {
  const controller = useMapController();
  useEffect(() => {
    if (!controller) return;
    const update = (): void => {
      const bbox = controller.selectionBbox(BOX_W, BOX_H);
      onEstimate(combinedEstimate(bbox), bbox);
    };
    update();
    return controller.onViewChange(update);
  }, [controller, onEstimate]);
  return null;
}

export function RegionPicker({ onClose }: { onClose: () => void }): JSX.Element {
  const [estimate, setEstimate] = useState<DownloadEstimate | null>(null);
  const bboxRef = useRef<Bbox | null>(null);
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<Phase>('pick');
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const tooLarge = estimate != null && estimate.tileCount > REGION_MAX_TILES;

  const onEstimate = useMemo(
    () => (e: DownloadEstimate, b: Bbox) => {
      setEstimate(e);
      bboxRef.current = b;
    },
    [],
  );

  const start = async (): Promise<void> => {
    const bbox = bboxRef.current;
    if (!bbox || tooLarge) return;
    setPhase('downloading');
    const [token, depthEdition] = await Promise.all([requestToken(bbox), loadDepthEdition()]);
    const handle = startRegionDownload(
      {
        id: uuid(),
        name: name.trim() || 'Kartområde',
        bbox,
        zMin: REGION_ZOOM_MIN,
        zMax: REGION_ZOOM_MAX,
        token,
        basePath: baseTilePath(useBaseLayerStore.getState().source),
        depthEdition,
      },
      setProgress,
    );
    cancelRef.current = handle.cancel;
    await handle.promise;
    setPhase('done');
  };

  const pct = progress ? Math.round((progress.done / Math.max(1, progress.total)) * 100) : 0;

  return (
    <div className="region-picker">
      <div className="region-picker__map">
        <MapShell>
          <Estimator onEstimate={onEstimate} />
          <div className="region-picker__box" style={{ width: `${BOX_W * 100}%`, height: `${BOX_H * 100}%` }} />
        </MapShell>
      </div>

      <div className="region-picker__panel">
        {phase === 'pick' && (
          <>
            <p className="region-picker__hint">{msg.region.hint}</p>
            <div className="region-picker__estimate">
              <span className="numeral">
                {estimate ? formatNumber(estimate.tileCount, 0) : '—'}
              </span>{' '}
              {msg.settings.tiles} · {estimate ? formatBytes(estimate.bytes) : '—'}
            </div>
            {tooLarge && <p className="region-picker__warn">{msg.region.tooLarge}</p>}
            <input
              className="region-picker__input"
              placeholder={msg.region.name}
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="region-picker__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {msg.region.cancel}
              </button>
              <button type="button" className="btn btn--accent" disabled={tooLarge} onClick={() => void start()}>
                {msg.region.download}
              </button>
            </div>
          </>
        )}

        {phase === 'downloading' && (
          <>
            <p className="region-picker__hint">{msg.region.downloading}</p>
            <div className="region-picker__bar">
              <div className="region-picker__bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="region-picker__estimate numeral">
              {progress ? `${progress.done} / ${progress.total}` : ''} · {pct}%
            </div>
            <div className="region-picker__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  cancelRef.current?.();
                  onClose();
                }}
              >
                {msg.region.cancel}
              </button>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            <p className="region-picker__hint">{msg.region.done} ✓</p>
            <div className="region-picker__actions">
              <button type="button" className="btn btn--accent" onClick={onClose}>
                {msg.region.close}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
